import { Card, CardColor, CardType, GamePhase, GameState, Player, PreparedCard, Tower } from '../types'
import { ABSOLUTE_MAX_PREPARED_CARDS } from '../cardCatalog'
import { ensureDealingDeck, drawFromDealingDeck, shuffle } from './decks'
import { isImmuneToDamage, checkAndTriggerLifeCharger, applyHealthLoss } from './effects'
import { getEffectiveHero } from './heroes'

// Logging disabled to improve FPS
let detailedLogging = false

const log = (tag: string, message: string, data?: any) => {
  if (detailedLogging) {
    const timestamp = new Date().toISOString().split('T')[1].slice(0, 12)
    if (data !== undefined) {
      console.groupCollapsed(`[${timestamp}] [${tag}] ${message}`)
      console.log(JSON.parse(JSON.stringify(data)))
      console.groupEnd()
    } else {
      console.log(`[${timestamp}] [${tag}] ${message}`)
    }
  }
}

export const startStashChoicePhase = (state: GameState): GameState => {
  log('startStashChoice', 'Starting Stash Choice phase.')
  // IMPORTANT: Deep copy stashDeck to prevent mutating original state (critical for multiplayer sync)
  let newStashDeck = [...state.stashDeck]
  let newStashDiscard = [...state.stashDiscard]
  let newBurnedStashCards = [...state.burnedStashCards]
  const perPlayerCount = 5
  const totalNeeded = perPlayerCount * 2

  // Debug: Log current card pool sizes
  console.log(`[StashChoice] Deck: ${newStashDeck.length}, Discard: ${newStashDiscard.length}, Burned: ${newBurnedStashCards.length}`)

  // Reshuffle burned and discarded cards back into deck if needed
  if (newStashDeck.length < totalNeeded) {
    const discardCount = newStashDiscard.length
    const burnedCount = newBurnedStashCards.length
    console.log(`[StashChoice] Reshuffling ${discardCount} discard + ${burnedCount} burned cards into deck`)

    const cardsToReshuffle = [...newStashDiscard, ...newBurnedStashCards]
    if (cardsToReshuffle.length > 0) {
      newStashDeck = [...newStashDeck, ...cardsToReshuffle]
      newStashDiscard = []
      newBurnedStashCards = []
      console.log(`[StashChoice] After reshuffle, deck has ${newStashDeck.length} cards`)
    }
  }

  // If still not enough cards, skip the phase entirely
  if (newStashDeck.length < totalNeeded) {
    console.log(`[StashChoice] SKIPPING - only ${newStashDeck.length} cards available (need ${totalNeeded})`)
    return transitionToPreparationPhase({
      ...state,
      stashDeck: newStashDeck,
      stashDiscard: newStashDiscard,
      burnedStashCards: newBurnedStashCards,
      stashChoiceOptions: {},
      stashChoicePicksRemaining: undefined,
      pendingExtraDayCount: 0, // Clear to prevent infinite loop in startCombat
      log: [...state.log, 't:log.stashExhausted'],
    })
  }

  const p1 = state.players[0].id
  const p2 = state.players[1].id
  const optionsP1 = newStashDeck.splice(0, Math.min(perPlayerCount, newStashDeck.length))
  const optionsP2 = newStashDeck.splice(0, Math.min(perPlayerCount, newStashDeck.length))
  log('startStashChoice', 'Stash options presented.', { optionsP1, optionsP2 })
  const picksMap: Record<number, number> = {
    [p1]: state.players[0].playerClass === 'MAGE' ? 2 : 1,
    [p2]: state.players[1].playerClass === 'MAGE' ? 2 : 1,
  }
  return {
    ...state,
    stashDeck: newStashDeck,
    stashDiscard: newStashDiscard,
    burnedStashCards: newBurnedStashCards,
    stashChoiceOptions: { [p1]: optionsP1, [p2]: optionsP2 },
    stashChoicePicksRemaining: picksMap,
    phase: GamePhase.StashChoice,
    activations: state.activations || [],
  }
}

const resolveOverflowForAI = (state: GameState): GameState => {
  return state; // Deprecated: Logic moved to App.tsx and gameLogic.ts returnLockedCardFromOverflow
}

export const transitionToPreparationPhase = (state: GameState): GameState => {
  log('transitionToPrep', `Entering Preparation Phase for Round ${state.round === 1 ? 1 : state.round + 1}.`)
  let newState: GameState = JSON.parse(JSON.stringify(state))
  let newLog = [...state.log]
  if (newState.round > 1) {
    let playersCopy: [Player, Player] = JSON.parse(JSON.stringify(newState.players))
    playersCopy.forEach(player => {
      player.hasPassedActiveTurn = false
    })
    playersCopy.forEach((player, idx) => {
      const cardsToKeepInPrep: PreparedCard[] = []
      const newPlayerHand = [...player.hand]

      // Handle return of long-duration effect cards (Invincible)
      // Duration: Night (played) -> Day -> Night -> Return at start of next Day
      // This means if appliedRound is X, and current round is X+2, it should return.
      const longDurationEffects = [CardType.Invincible]
      const effectsToReturn = player.effectCards.filter(c =>
        longDurationEffects.includes(c.type) &&
        (newState.round - (c.appliedRound || 0) >= 2)
      )

      if (effectsToReturn.length > 0) {
        // Remove from effectCards
        player.effectCards = player.effectCards.filter(c => !effectsToReturn.includes(c))

        effectsToReturn.forEach(card => {
          // Check if it should go to Stash Discard (Mage Spell from Hand)
          // We approximate "Mage Spell from Hand" by checking if it's a Spell with essenceCost and player is Mage
          // Note: This is an approximation. Ideally we'd track source.
          const isMageSpell = player.playerClass === 'MAGE' && card.subType === 'Spell' && !!card.essenceCost

          if (isMageSpell) {
            newState.stashDiscard.push(card)
            newLog.push(`t:log.effectExpireDiscard|playerName=${player.name}|cardType=${card.type}`)
          } else {
            newPlayerHand.push(card)
            newLog.push(`t:log.effectExpireReturn|playerName=${player.name}|cardType=${card.type}`)
          }
        })
      }

      // Clean up expired DiscerningEye effects (applied 1+ rounds ago)
      // Duration: Night N (apply) → Day N+1 (visible) → Night N+1 (visible/locked) → Day N+2 (remove)
      // At Day N+2: newState.round = N+2, appliedRound = N, so roundsSinceApplied = 2
      // But we want to remove at Day N+2, which means roundsSinceApplied >= 2 at Day start
      const discerningEyeToRemove = player.effectCards.filter(c =>
        c.type === CardType.DiscerningEye &&
        (newState.round - (c.appliedRound || 0) >= 2)
      )
      if (discerningEyeToRemove.length > 0) {
        player.effectCards = player.effectCards.filter(c => !discerningEyeToRemove.includes(c))
        log('transitionToPrep', `Removed expired DiscerningEye effect for Player ${player.id}.`)
      }

      player.prepared.forEach(pc => {
        if (pc.lockedState) {
          const roundsSinceLocked = newState.round - pc.lockedState.appliedRound
          const effectiveDays = roundsSinceLocked + (newState.extraDaysInCurrentRound || 0)
          if (effectiveDays >= 2) {
            newPlayerHand.push(pc.card)
            newLog.push(`t:log.lockedReturn|playerName=${player.name}|cardType=${pc.card.type}`)
          } else {
            cardsToKeepInPrep.push(pc)
          }
        } else {
          cardsToKeepInPrep.push(pc)
        }
      })
      playersCopy[idx].prepared = cardsToKeepInPrep
      playersCopy[idx].hand = newPlayerHand
    })
    for (let idx = 0; idx < playersCopy.length; idx++) {
      const player = playersCopy[idx]
      const lockedCards = player.prepared.filter(pc => pc.lockedState)
      const availableSlots = getAvailablePrepareSlots(newState, player.id, GamePhase.Preparation)
      // Debug: Log when locked cards exist
      if (lockedCards.length > 0) {
        console.log(`[DEBUG] transitionToPrep: Player ${player.id} (${player.name}) has ${lockedCards.length} locked cards, availableSlots=${availableSlots}, isNeuralAgent=${player.isNeuralAgent}`)
      }
      if (lockedCards.length > availableSlots) {
        console.log(`[DEBUG] OVERFLOW DETECTED! Player ${player.id}: ${lockedCards.length} locked > ${availableSlots} slots`)
        newState.players = playersCopy
        newState.log = [...newLog, `t:log.chooseLocked|playerName=${player.name}`]
        // Neural agents go through choice phase; rule-based AI auto-resolves
        if (player.isAI && !player.isNeuralAgent) {
          console.log(`[DEBUG] Setting LockedCardOverflow phase for Rule-Based AI player ${player.id} to let App.tsx handle it`)
          return {
            ...newState,
            phase: GamePhase.LockedCardOverflow,
            overflowPlayerId: player.id,
            overflowMaxSlots: availableSlots,
          }
        } else {
          console.log(`[DEBUG] Setting LockedCardOverflow phase for Neural agent player ${player.id}`)
          return {
            ...newState,
            phase: GamePhase.LockedCardOverflow,
            overflowPlayerId: player.id,
            overflowMaxSlots: availableSlots,
          }
        }
      }
    }
    newState.players = playersCopy
    const deathByDaily: Set<number> = new Set()
    for (let i = 0; i < playersCopy.length; i++) {
      const player = playersCopy[i]
      const playerIsImmune = isImmuneToDamage(player, newState.round)
      if (playerIsImmune) {
        newLog.push(`t:log.immuneDisease|playerName=${player.name}`)
        continue
      }
      const diseaseCards = player.effectCards.filter(c => c.type === CardType.Disease)
      const naturalGripCards = player.effectCards.filter(c => c.type === CardType.NaturalGrip)
      if (diseaseCards.length > 0 || naturalGripCards.length > 0) {
        // DEBUG: Track hero values for Nyrix persistence bug investigation
        console.log('[DEBUG-NYRIX] Disease damage check:', {
          targetPlayer: player.name,
          targetHero: player.hero,
          p1Hero: playersCopy[0].hero,
          p2Hero: playersCopy[1].hero,
          round: newState.round,
          diseaseCount: diseaseCards.length,
          diseaseOwnerIds: diseaseCards.map(c => c.ownerId)
        })
        const totalDiseaseDamage = diseaseCards.reduce((sum, card) => {
          const owner = playersCopy.find(p => p.id === card.ownerId)
          const baseDamage = card.value || 1
          const ownerEffectiveHero = owner ? getEffectiveHero(owner, player) : null
          const multiplier = ownerEffectiveHero === 'NYRIX' ? 2 : 1
          // DEBUG: Track Nyrix multiplier calculation
          console.log('[DEBUG-NYRIX] Disease multiplier calc:', {
            diseaseOwnerId: card.ownerId,
            ownerHero: owner?.hero,
            ownerEffectiveHero,
            multiplier,
            baseDamage
          })
          return sum + (baseDamage * multiplier)
        }, 0)
        const totalNaturalGripDamage = naturalGripCards.reduce((sum, card) => sum + (card.value || 2), 0)
        const voodooBonus = diseaseCards.filter(dc => {
          const owner = playersCopy.find(p => p.id === dc.ownerId)
          return !!owner && owner.effectCards.some(c => c.type === CardType.VoodooDoll)
        }).length
        const totalDailyDamage = totalDiseaseDamage + totalNaturalGripDamage + voodooBonus
        const cursedIronCards = player.effectCards.filter(c => c.type === CardType.CursedIron)
        const damageReduction = cursedIronCards.reduce((acc, card) => acc + (card.value || 0), 0)
        let finalDailyDamage = totalDailyDamage
        if (damageReduction > 0) {
          finalDailyDamage = Math.max(0, totalDailyDamage - damageReduction)
          newLog.push(`t:log.cursedIronReduce|playerName=${player.name}|damage=${damageReduction}`)
        }
        const damageLogs = applyHealthLoss(player, finalDailyDamage, playersCopy.find(p => p.id !== player.id))
        newLog.push(...damageLogs)
        if (player.health <= 0) {
          const stateBeforeCheck = { ...newState, players: playersCopy, log: newLog }
          const stateAfterCheck = checkAndTriggerLifeCharger(stateBeforeCheck, player.id)
          if (stateAfterCheck.players[i].health > 0) {
            playersCopy = stateAfterCheck.players as [Player, Player]
            newLog = stateAfterCheck.log
            newState.players = playersCopy
          } else {
            deathByDaily.add(player.id)
          }
        }
      }
    }
    if (deathByDaily.size > 0) {
      const [p1Daily, p2Daily] = playersCopy
      if (p1Daily.health <= 0 && p2Daily.health <= 0) {
        newLog.push(`t:log.succumbed|playerName=${p1Daily.name}`)
        newLog.push(`t:log.succumbed|playerName=${p2Daily.name}`)
        newLog.push("t:log.draw")
        return { ...newState, players: playersCopy as [Player, Player], phase: GamePhase.GameOver, winner: null, log: newLog }
      }
      const deadPlayer = p1Daily.health <= 0 ? p1Daily : p2Daily.health <= 0 ? p2Daily : null
      if (deadPlayer) {
        const opponent = playersCopy.find(p => p.id !== deadPlayer!.id)!
        newLog.push(`t:log.succumbed|playerName=${deadPlayer.name}`)
        newLog.push(`t:log.victorious|playerName=${opponent.name}`)
        return { ...newState, players: playersCopy as [Player, Player], phase: GamePhase.GameOver, winner: opponent, log: newLog, pendingVictory: { winnerId: opponent.id } }
      }
    }
    newState.players = playersCopy

    // After Disease/NaturalGrip check is complete, unprepare any remaining Life Charger cards
    // (They were kept prepared specifically for the disease check)
    playersCopy.forEach(player => {
      const lifeChargerCards = player.prepared.filter(pc => pc.card.type === CardType.LifeCharger)
      lifeChargerCards.forEach(pc => {
        player.hand.push(pc.card)
        newLog.push(`t:log.effectExpireReturn|playerName=${player.name}|cardType=Life Charger`)
      })
      player.prepared = player.prepared.filter(pc => pc.card.type !== CardType.LifeCharger)
    })

    // DRAW ORDER: Sequence follows firstPlayerIdForNight
    const playersInOrder = [...newState.players].sort((a, b) => {
      if (a.id === newState.firstPlayerIdForNight) return -1
      if (b.id === newState.firstPlayerIdForNight) return 1
      return 0
    })

    playersInOrder.forEach(player => {
      const isSkipping = player.effectCards.some(c => c.type === CardType.Hibernation || c.type === CardType.Frost)
      if (!isSkipping) {
        player.towers.forEach((tower: Tower) => {
          const deck = newState.colorDecks[tower.color]
          if (deck && deck.length > 0) {
            const card = { ...deck[deck.length - 1], ownerId: player.id }
            newState.colorDecks[tower.color] = deck.slice(0, -1)
            player.hand.push(card)
            newLog.push(`t:log.drawFromTower|playerName=${player.name}|towerColor=${tower.color}`)
          }
        })
      } else {
        const reason = player.effectCards.some(c => c.type === CardType.Frost) ? 'Frost' : 'Hibernation'
        newLog.push(`t:log.skippingTowers|playerName=${player.name}|reason=${reason}`)
      }
    })

    const p1 = newState.players.find(p => p.id === 1)!
    const p2 = newState.players.find(p => p.id === 2)!
    const p1IsSkipping = p1.effectCards.some(c => c.type === CardType.Hibernation || c.type === CardType.Frost)
    const p2IsSkipping = p2.effectCards.some(c => c.type === CardType.Hibernation || c.type === CardType.Frost)
    
    const p1DrawCount = p1IsSkipping ? 0 : 3 + p1.bonusDraw
    const p2DrawCount = p2IsSkipping ? 0 : 3 + p2.bonusDraw
    
    if (p1IsSkipping) newLog.push(`t:log.skippingDeck|playerName=${p1.name}`)
    if (p2IsSkipping) newLog.push(`t:log.skippingDeck|playerName=${p2.name}`)
    
    const requiredCards = p1DrawCount + p2DrawCount
    if (requiredCards > 0) {
      newState = ensureDealingDeck(newState, requiredCards)
      // SKIP LOGIC: If dealing deck cannot fulfill the required cards for both players, skip distribution
      if (newState.dealingDeck.length < requiredCards) {
        newLog.push(`t:log.dealingDeckExhausted`)
      } else {
        if (p1DrawCount > 0) newState = drawFromDealingDeck(newState, 1, p1DrawCount)
        if (p2DrawCount > 0) newState = drawFromDealingDeck(newState, 2, p2DrawCount)
      }
    }
  }
  newLog.push(`t:term.prepare-for-battle`)
  const playersDedup: [Player, Player] = JSON.parse(JSON.stringify(newState.players))
  playersDedup.forEach(p => {
    const seen = new Set<string>()
    p.hand = p.hand.filter(c => {
      if (seen.has(c.id)) return false
      seen.add(c.id)
      return true
    })
    if (p.hero === 'ASTRID') {
      p.astridCharges = {
        [CardColor.Red]: 0,
        [CardColor.Pink]: 0,
        [CardColor.Green]: 0,
        [CardColor.Black]: 0,
        [CardColor.Purple]: 0,
        [CardColor.Yellow]: 0,
      }
    }
  })
  return { ...newState, players: playersDedup, phase: GamePhase.Preparation, log: newLog, discardMode: {}, activations: newState.activations || [] }
}

export const startCombat = (state: GameState): GameState => {
  log('startCombat', `Starting Combat for Round ${state.round}.`)
  if ((state.pendingExtraDayCount || 0) > 0) {
    const remaining = (state.pendingExtraDayCount || 0) - 1
    const playersReset = state.players.map(p => ({ ...p, disruptedSlots: 0 })) as [Player, Player]
    const baseState = { ...state, players: playersReset, pendingExtraDayCount: remaining, extraDaysInCurrentRound: (state.extraDaysInCurrentRound || 0) + 1 }
    const stashState = startStashChoicePhase(baseState)
    stashState.log = [...(state.log || []), `t:log.twilightShift`]
    return stashState
  }
  let playersCopy: [Player, Player] = JSON.parse(JSON.stringify(state.players))
  let newColorDecks: Record<CardColor, Card[]> = JSON.parse(JSON.stringify(state.colorDecks))
  const effectCardsToReturn: Card[] = []
  playersCopy.forEach(player => {
    const temporaryEffects = [CardType.Hibernation, CardType.SealOfSilence]
    // Note: Invincible lasts for Night -> Day -> Night, so it is handled in transitionToPreparationPhase (Day start)
    // to check if its duration (2 rounds) has expired.
    // SealOfSilence only lasts for the current night, so it's a temporary effect.
    const cardsToReturn = player.effectCards.filter(c => temporaryEffects.includes(c.type))
    if (cardsToReturn.length > 0) {
      log('startCombat', `Clearing temporary effect cards for Player ${player.id}.`, { cardsToReturn })
    }
    effectCardsToReturn.push(...cardsToReturn)
    player.effectCards = player.effectCards.filter(c => !temporaryEffects.includes(c.type))

    // DiscerningEye cleanup happens in transitionToPreparationPhase (Day start)
    // Here we only handle the locking logic for active effects
    const discerningEyeEffect = player.effectCards.find(c => c.type === CardType.DiscerningEye)
    if (discerningEyeEffect && discerningEyeEffect.appliedRound === state.round - 1) {
      console.log(`[DEBUG] DiscerningEye LOCKING ${player.prepared.length} cards for Player ${player.id} (${player.name}) at round ${state.round}`)
      player.prepared = player.prepared.map(pc => ({ ...pc, lockedState: { appliedRound: state.round - 1 } }))
      log('startCombat', `DiscerningEye locks ${player.prepared.length} cards for Player ${player.id}.`)
    }
  })
  if (effectCardsToReturn.length > 0) {
    const returnedToColors = new Set<CardColor>()
    effectCardsToReturn.forEach(card => {
      newColorDecks[card.color].push(card)
      returnedToColors.add(card.color)
    })
    returnedToColors.forEach(color => {
      newColorDecks[color] = shuffle(newColorDecks[color], state.seed + '_effect_return_' + color)
    })
  }
  log('startCombat', 'Resetting player combat stats (shield, resolved, etc).')
  playersCopy[0].shield = 0
  playersCopy[0].resolved = []
  playersCopy[0].disruptedSlots = 0
  playersCopy[0].capturedTowerThisTurn = false
  playersCopy[0].changedTowerColorThisTurn = false
  playersCopy[0].hasPassedActiveTurn = false
  playersCopy[1].shield = 0
  playersCopy[1].resolved = []
  playersCopy[1].disruptedSlots = 0
  playersCopy[1].capturedTowerThisTurn = false
  playersCopy[1].changedTowerColorThisTurn = false
  playersCopy[1].hasPassedActiveTurn = false
  log('startCombat', 'Combat phase started. Player 1 turn.')
  return {
    ...state,
    players: playersCopy,
    colorDecks: newColorDecks,
    phase: GamePhase.Combat,
    turnPlayerId: state.firstPlayerIdForNight,
    consecutivePasses: 0,
    discardMode: {},
    activations: [], // FIX: Ensure clean activation state at combat start
    acceptedActivations: [], // FIX: Clear accepted activations too
    log: [...state.log, `t:log.nightMarker|round=${state.round}`],
    preparedAtNightStart: {
      [playersCopy[0].id]: playersCopy[0].prepared.length,
      [playersCopy[1].id]: playersCopy[1].prepared.length,
    },
    damageCardsUsedThisNight: {},
    attemptedDamageCardsThisNight: {},
  }
}

export const startNewRound = (state: GameState): GameState => {
  const newRound = state.round + 1
  log('startNewRound', `Starting Round ${newRound}.`)
  const newPlayers = state.players.map(p => ({
    ...p,
    resolved: [],
    shield: 0,
    essence: Math.max(0, p.essence) // Clamp essence to 0 to prevent negative essence debt causing AI deadlocks
  })) as [Player, Player]
  const newFirstPlayerIdForNight = state.firstPlayerIdForNight === 1 ? 2 : 1
  log('startNewRound', `Next night will start with Player ${newFirstPlayerIdForNight}`)
  const newState = {
    ...state,
    players: newPlayers,
    round: newRound,
    firstPlayerIdForNight: newFirstPlayerIdForNight,
    log: [`t:log.roundStart|round=${newRound}`],
    drawEvents: [],
    discardMode: {},
    extraDaysInCurrentRound: 0,
  }

  const lorianPlayer = newState.players.find(p => p.hero === 'LORIAN');
  if (lorianPlayer) {
    return {
      ...newState,
      phase: GamePhase.LorianClassChoice,
      log: [...newState.log, `t:log.lorianChoice|playerName=${lorianPlayer.name}`]
    };
  }

  return startStashChoicePhase(newState)
}

export const assignVictoryPoint = (state: GameState, winnerId: number, zoneId: number, factionId: number = 0, basePoints?: { harmonized: number, chaotic: number, untouched: number }): GameState => {
  // Check if this is a single-player AI game - if so, don't affect world map
  const isSinglePlayerAI = state.players.some(p => p.isAI);

  if (isSinglePlayerAI) {
    if (detailedLogging) console.log('[assignVictoryPoint] Skipping world map update for AI game');
    return { ...state, pendingVictory: null };
  }

  const mp = { ...(state.mapPoints || {}) }
  const mc = { ...(state.mapControl || {}) }

  // Use basePoints to initialize if provided, trusting server state as the baseline
  if (basePoints) {
    mp[zoneId] = { ...basePoints };
  } else if (!mp[zoneId]) {
    mp[zoneId] = { harmonized: 0, chaotic: 0, untouched: 0 };
  }

  const faction = factionId;

  if (detailedLogging) console.log('[assignVictoryPoint] Winner:', winnerId, 'Faction:', faction, 'Zone:', zoneId);

  // Increment points for the winning faction
  if (faction === 1) mp[zoneId].harmonized += 1
  else if (faction === 2) mp[zoneId].chaotic += 1
  else if (faction === 3) mp[zoneId].untouched += 1

  const h = mp[zoneId].harmonized
  const c = mp[zoneId].chaotic
  const u = mp[zoneId].untouched

  let control: 0 | 1 | 2 | 3 = 0
  // Strict inequality ensures that if points are equal (draw), it remains Neutral (0).
  if (h > c && h > u) control = 1
  else if (c > h && c > u) control = 2
  else if (u > h && u > c) control = 3

  // Explicitly set neutral if there's a tie for first place
  // The logic above naturally defaults to 0 if the highest score is shared (e.g. h=5, c=5, u=2 => h>c is false, c>h is false).
  // Confirmed: 5 > 5 is false. So if h == c and both > u, it falls through to 0. Correct.

  mc[zoneId] = control
  const newState: GameState = { ...state, mapPoints: mp, mapControl: mc, pendingVictory: null }
  const controlStr = control === 1 ? 't:log.pureHarmonized' : control === 2 ? 't:log.pureChaotic' : control === 3 ? 't:log.pureUntouched' : 't:log.neutral'
  const logLine = `t:log.zonePoint|zoneId=${zoneId}|control=${controlStr}`
  newState.log = [...(newState.log || []), logLine]
  return newState
}


export const getAvailablePrepareSlots = (state: GameState, playerId: number, phase?: GamePhase): number => {
  const player = state.players.find(p => p.id === playerId)!;
  const opponent = state.players.find(p => p.id !== playerId)!;
  const hasPreparedArmory = player.prepared.some(pc => !!pc.card.essenceCost && pc.card.subType === 'Armory');
  const warriorBonus = player.playerClass === 'WARRIOR' && phase === GamePhase.Preparation && hasPreparedArmory ? 1 : 0;
  const effectiveHero = getEffectiveHero(player, opponent);
  // EROK_BURU: +1 slot per 2 self HP lost (AI hero)
  const erokBonus = effectiveHero === 'EROK_BURU' ? Math.floor((player.maxHealth - player.health) / 2) : 0;
  // VERG: +1 slot per 3 opponent HP lost (player hero)
  const vergBonus = effectiveHero === 'VERG' ? Math.floor((opponent.maxHealth - opponent.health) / 3) : 0;
  const heroBonus = erokBonus + vergBonus;
  return Math.min(ABSOLUTE_MAX_PREPARED_CARDS, (player.maxPrepareSlots - player.disruptedSlots) + warriorBonus + heroBonus);
};
